<template>
  <div class="fullscreen modal is-active">
    <div class="modal-background" @click="close"></div>
    <div ref="container" class="modal-content" @click="() => { if (!this.clickedImage) this.close() }">
      <img v-show="loaded" ref="image" :src="src" alt="" :style="style" :class="imageClass" @click="clickImage" />
      <div v-if="!loaded" class="loader"></div>
    </div>
    <div class="modal-buttons">
      <span v-if="showArrows" class="is-hidden-mobile">
        <button class="icon-button custom-icon" @click.stop="$emit('goToSlide', (position - 1))"><span class="icon"><i class="fal fa-chevron-left"  aria-label="previous"></i></span></button>
        <button class="icon-button custom-icon" @click.stop="$emit('goToSlide', (position + 1))"><span class="icon"><i class="fal fa-chevron-right"  aria-label="next"></i></span></button>
      </span>
      <span class="is-hidden-desktop mobile-text">{{mobileText}}:</span>
      <icon v-show="displayPostAnnotate" type="post" class="custom-icon" @annotate="annotate"></icon>
      <icon v-if="canAnnotate" type="comment" class="custom-icon" @annotate="annotate"></icon>
      <button class="icon-button" @click="close"><span class="icon"><i class="fal fa-times"  aria-label="close"></i></span></button>
    </div>
  </div>
</template>

<script>
import Icon from './Icon'
import panZoom from 'pan-zoom'
import i18n from '@/i18n'
import auth from '@/mixins/auth'

export default {
  components: {
    Icon
  },

  props: ['src', 'canAnnotate', 'canCreate', 'position', 'showArrows', 'project'],
  mixins: [auth],
  data: function () {
    return {
      reset: null,
      image: { width: 0, height: 0, scale: 1 },
      container: { width: 0, height: 0 },
      zoom: 1,
      pan: { x: 0, y: 0 },
      clickedImage: false,
      clickImageTimer: null,
      loaded: false
    }
  },

  computed: {
    displayPostAnnotate: function () {
      return (this.isAdmin || this.projectRole === 'anonAdmin' || (this.isProjectEditable && !this.isProjectSubmitted)) && (this.canCreate && this.canAnnotate)
    },
    isProjectSubmitted: function () {
      return this.project.submitted
    },
    isProjectEditable: function () {
      return this.project.isEditable
    },
    style: function () {
      return `transform: scale(${this.zoom}) translate(${this.pan.x}px, ${this.pan.y}px)`
    },
    imageClass: function () {
      if (this.container.height < this.container.width) {
        const scaleY = this.container.height / this.image.height
        if (this.image.width * scaleY > this.container.width) {
          return { fullWidth: true }
        }

        return { fullHeight: true }
      }

      const scaleX = this.container.width / this.image.width
      if (this.image.height * scaleX > this.container.height) {
        return { fullHeight: true }
      }

      return { fullWidth: true }
    },
    canPanX: function () {
      return this.image.width * this.zoom > this.container.width
    },
    canPanY: function () {
      return this.image.height * this.zoom > this.container.height
    },
    mobileText: function () {
      return window.screen.width <= 320 ? i18n.t('projectPage.remix') : i18n.t('projectPage.remixAsNew')
    }
  },

  created: function () {
    window.addEventListener('keydown', this.handleKeyPress)
  },

  mounted: function () {
    this.init()
  },

  beforeDestroy: function () {
    if (this.reset) {
      this.reset()
    }
    window.removeEventListener('keydown', this.handleKeyPress)
  },

  methods: {
    init: function () {
      const image = this.$refs.image
      if (!image || !image.complete) {
        setTimeout(() => this.init(), 250)
        return
      }

      this.loaded = true

      this.reset = panZoom(this.$refs.container, this.update)

      const boundingRect = this.$refs.container.getBoundingClientRect()

      this.container.width = boundingRect.width
      this.container.height = boundingRect.height

      this.image.width = image.naturalWidth
      this.image.height = image.naturalHeight

      if (this.imageClass.fullWidth) {
        this.image.scale = this.container.width / this.image.width
        this.image.width = this.container.width
        this.image.height = this.image.height * this.image.scale
      } else {
        this.image.scale = this.container.height / this.image.height
        this.image.width = this.image.width * this.image.scale
        this.image.height = this.container.height
      }

      if (this.image.scale < 0.1) {
        this.zoom = 2
      }
    },

    close: function () {
      this.$emit('close')
    },

    update: function (e) {
      this.clickImage()

      this.zoom -= e.dz / 50
      if (this.zoom < 1) {
        this.zoom = 1
        this.pan.x = 0
        this.pan.y = 0
      }

      if (this.zoom > 10) {
        this.zoom = 10
      }

      if (this.canPanX) {
        this.pan.x += e.dx / this.zoom
      }

      if (this.canPanY) {
        this.pan.y += e.dy / this.zoom
      }
    },

    clickImage: function () {
      this.clickedImage = true
      if (this.clickImageTimer) {
        clearTimeout(this.clickImageTimer)
      }
      this.clickImageTimer = setTimeout(() => {
        this.clickedImage = false
      }, 100)
    },

    annotate: function (type) {
      this.close()
      this.$emit('annotate', type)
    },

    handleKeyPress: function (keyCode) {
      if (keyCode.code === 'ArrowLeft') {
        this.$emit('goToSlide', (this.position - 1))
      }
      if (keyCode.code === 'ArrowRight') {
        this.$emit('goToSlide', (this.position + 1))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  .loader {
    margin: auto;
    font-size: 2rem;
  }

  .modal {
    .modal-content {
      position: absolute;
      text-align: center;
      top: 80px;
      left: 20px;
      bottom: 20px;
      right: 20px;
      width: auto;
      height: auto;
      align-items: center;
      display: flex;

      @include mobile {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        top: 0;
        margin: 0;
        width: auto;
        height: auto;
        max-height: 100%;
        max-width: 100%;
      }

      img {
        margin: auto;
        display: block;

        &.fullHeight {
          width: auto;
          height: 100%;
        }

        &.fullWidth {
          width: 100%;
          height: auto;
        }
      }
    }

    .modal-buttons {
      z-index: 5;
      position: fixed;
      right: 20px;
      top: 20px;
      display: flex;
      justify-content: flex-end;
      color: white;
      font-size: 0.75rem;

      @include mobile {
        top: 10px;
        right: 10px;
      }

      .mobile-text {
        line-height: 2.5rem;
        padding-right: 0.75rem;
      }

      .custom-icon {
        margin-right: 2rem;
        @include mobile {
          margin-right: 0.75rem;
        }
      }

      .icon-button {
        cursor: pointer;
        background: none;
        border: none;
        color: white;
        font-size: 2rem;
        @include mobile {
          padding: 0.25rem;
          width: auto;
        }
      }
    }
  }
</style>
